import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotification } from '../../sharedComponents/Notifications';
import { X, Loader2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { authorAuthService, Category } from '../services/authorAuth';
import { useInitializeSubmissionPage } from '../hooks/useInitializeSubmissionPage';

const ApplicationForJournalSupport: React.FC = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<number>(1);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    id: '00000000-0000-0000-0000-000000000000',
    title: '',
    authors: '',
    positionOfJournalRepresentative: '',
    journalProfessionalAssociation: '',
    isbnNo: '',
    edition: '',
    keywords: '',
    applicationType: 2,
    categoryId: 1,
    abstract: '',
    issue: '',
    publicationUrl: '',
    volume: '',
    dateOfJournalCommencement: '',
    editorialBoardMembers: [{ name: '', address: '' }] as { name: string; address: string }[],
    physicalAddress: '',
    associationBodyUrl: '',
    journalAccessibility: '' as '' | '1' | '2' | '3', // 1=Fully open, 2=Partially, 3=Not available
    uploadedFile: null as File | null,
    fileName: '',
    fileType: '',
    fileSize: 0,
    guidelinesFile: null as File | null,
    guidelinesFileName: '',
    guidelinesFileSize: 0,
    policyFile: null as File | null,
    policyFileName: '',
    policyFileSize: 0,
  });

  useInitializeSubmissionPage(setCategories, setIsLoading, '/author');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'categoryId' || name === 'applicationType' ? Number(value) : value
    }));
  };

  // ── Editorial board members helpers ────────────────────────────────────────
  const MAX_EDITORIAL = 10;

  const updateBoardMember = (idx: number, field: 'name' | 'address', value: string) => {
    setFormData(prev => ({
      ...prev,
      editorialBoardMembers: prev.editorialBoardMembers.map((m, i) =>
        i === idx ? { ...m, [field]: value } : m
      ),
    }));
  };

  const addBoardMember = () => {
    setFormData(prev => prev.editorialBoardMembers.length >= MAX_EDITORIAL ? prev : {
      ...prev,
      editorialBoardMembers: [...prev.editorialBoardMembers, { name: '', address: '' }],
    });
  };

  const removeBoardMember = (idx: number) => {
    setFormData(prev => prev.editorialBoardMembers.length <= 1 ? prev : {
      ...prev,
      editorialBoardMembers: prev.editorialBoardMembers.filter((_, i) => i !== idx),
    });
  };

  const validBoardMembers = formData.editorialBoardMembers.filter(
    m => m.name.trim() !== '' && m.address.trim() !== ''
  );

  const isStep1Valid =
    formData.title.trim() &&
    formData.authors.trim() &&
    formData.abstract.trim() &&
    formData.physicalAddress.trim() &&
    formData.journalAccessibility !== '';

  const isStep2Valid =
    formData.uploadedFile !== null &&
    formData.guidelinesFile !== null &&
    formData.policyFile !== null;

  const steps = [
    { number: 1, label: 'Application Info' },
    { number: 2, label: 'Upload File' },
    { number: 3, label: 'Review & Submit' }
  ];

  const handleSubmit = async () => {
    if (!formData.uploadedFile) return;

    setIsSubmitting(true);
    try {
      const data = new FormData();
      const appendIfPresent = (key: string, value: string | null | undefined) => {
        if (value !== null && value !== undefined && value.toString().trim() !== '') {
          data.append(key, value.toString());
        }
      };

      // Required identifiers
      data.append('Id', formData.id);
      data.append('ApplicationType', formData.applicationType.toString());
      data.append('CategoryId', formData.categoryId.toString());
      data.append('ManuscriptFile', formData.uploadedFile);

      // Optional strings — omit when empty so the server reads null
      appendIfPresent('Title', formData.title);
      appendIfPresent('Authors', formData.authors);
      appendIfPresent('ISBNNo', formData.isbnNo);
      appendIfPresent('Edition', formData.edition);
      appendIfPresent('Keywords', formData.keywords);
      appendIfPresent('Abstract', formData.abstract);
      appendIfPresent('PositionOfJournalRepresentative', formData.positionOfJournalRepresentative);
      appendIfPresent('JournalProfessionalAssociation', formData.journalProfessionalAssociation);

      if (formData.applicationType === 2) {
        appendIfPresent('Issue', formData.issue);
        appendIfPresent('PublicationUrl', formData.publicationUrl);
        appendIfPresent('Volume', formData.volume);
        appendIfPresent('JournalBodyPhysicalAddress', formData.physicalAddress);
        appendIfPresent('JournalBodyWebsite', formData.associationBodyUrl);
        // Backend expects the display string, not a numeric enum.
        const accessibilityLabel =
          formData.journalAccessibility === '1' ? 'Fully open'
          : formData.journalAccessibility === '2' ? 'Partially'
          : formData.journalAccessibility === '3' ? 'Not available'
          : '';
        appendIfPresent('JournalBodyArticleAccessibility', accessibilityLabel);
        // `<input type="date">` gives back "YYYY-MM-DD"; backend expects an ISO
        // datetime, so append a midnight-UTC suffix when the field is filled.
        if (formData.dateOfJournalCommencement) {
          data.append(
            'DateOfJournalCommencement',
            new Date(`${formData.dateOfJournalCommencement}T00:00:00Z`).toISOString()
          );
        }
        // Editorial board members are persisted via the BookAuthors array
        // (per backend agreement): name = name+title, affiliateInstitution = address.
        // isLeadAuthor is intentionally omitted.
        validBoardMembers.forEach((m, i) => {
          data.append(`BookAuthors[${i}][Id]`, '00000000-0000-0000-0000-000000000000');
          data.append(`BookAuthors[${i}][Name]`, m.name.trim());
          data.append(`BookAuthors[${i}][AffiliateInstitution]`, m.address.trim());
        });
        // Supplementary uploads
        if (formData.guidelinesFile) {
          data.append('JournalBodyOperationalGuideLineFile', formData.guidelinesFile);
        }
        if (formData.policyFile) {
          data.append('JournalBodyPolicyFile', formData.policyFile);
        }
      }

      // NumberOfPages and BookAuthors are not collected on the journal flow → omit entirely

      const response = await authorAuthService.createUpdateSubmission(data);

      if (response.isSuccess) {
        showNotification('success', {
          title: 'Journal Submitted!',
          message: response.message || 'Your journal application has been submitted for TETFund TAG approval.',
          confirmButtonText: 'OK',
          onConfirm: () => navigate('/author')
        });
      } else {
        showNotification('error', {
          title: 'Submission Failed',
          message: response.message || 'There was an error submitting your journal application. Please try again.'
        });
      }
    } catch (error: any) {
      showNotification('error', {
        title: 'Submission Failed',
        message: error.response?.data?.message || error.message || 'There was an error submitting your journal application. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-[#088A61] animate-spin" />
          <p className="text-gray-600 font-medium">Loading ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="mb-0">
        <Link
          to="/author"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm transition-colors"
        >
          <i className="bi bi-arrow-left"></i>
          Back to Dashboard
        </Link>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Application for Journal Support
        </h1>
        {/* <p className="text-gray-600">
          Submit your journal support application for TETFund TAG review
        </p> */}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-center relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200" style={{ zIndex: 1 }}></div>

          {steps.map((step) => (
            <div key={step.number} className="flex flex-col items-center relative" style={{ zIndex: 2 }}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${step.number <= activeStep
                  ? 'bg-[#088A61] text-white'
                  : 'bg-gray-100 text-gray-400'
                  }`}
              >
                {step.number}
              </div>
              <span
                className={`mt-2 text-xs font-medium ${step.number <= activeStep ? 'text-[#088A61]' : 'text-gray-500'
                  }`}
              >
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {activeStep === 1 && (
          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Journal Support Application Information
              </h2>
              <p className="text-sm text-gray-600">
                Provide details about your journal support application
              </p>
            </div>

            <form className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
                  Name of Journal *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                  id="title"
                  name="title"
                  placeholder="Enter the name of the journal"
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* <div>
                  <label htmlFor="categoryId" className="block text-sm font-semibold text-gray-700 mb-2">
                    Thematic Area *
                  </label>
                  <select
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all appearance-none bg-white font-medium"
                    id="categoryId"
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleInputChange}
                    style={{
                      color: formData.categoryId !== 0 ? '#111827' : '#6B7280'
                    }}
                  >
                    <option value={0}>Select a thematic area</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div> */}
                <div>
                  <label htmlFor="authors" className="block text-sm font-semibold text-gray-700 mb-2">
                    Name of Journal Leader *
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                    id="authors"
                    name="authors"
                    placeholder="Enter the journal leader's name"
                    value={formData.authors}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="positionOfJournalRepresentative" className="block text-sm font-semibold text-gray-700 mb-2">
                    Position of Journal Representative *
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                    id="positionOfJournalRepresentative"
                    name="positionOfJournalRepresentative"
                    placeholder="e.g. Editor in Chief, Managing Editor, Secretary, Editor"
                    value={formData.positionOfJournalRepresentative}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label htmlFor="journalProfessionalAssociation" className="block text-sm font-semibold text-gray-700 mb-2">
                    Journal Professional Association *
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                    id="journalProfessionalAssociation"
                    name="journalProfessionalAssociation"
                    placeholder="Enter professional association"
                    value={formData.journalProfessionalAssociation}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="isbnNo" className="block text-sm font-semibold text-gray-700 mb-2">
                    ISSN No
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                    id="isbnNo"
                    name="isbnNo"
                    placeholder="Enter ISSN if available"
                    value={formData.isbnNo}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label htmlFor="edition" className="block text-sm font-semibold text-gray-700 mb-2">
                    Edition
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                    id="edition"
                    name="edition"
                    placeholder="e.g. 1st Edition, 2nd Edition"
                    value={formData.edition}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="volume" className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Volume
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                    id="volume"
                    name="volume"
                    placeholder="Enter current volume"
                    value={formData.volume}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label htmlFor="issue" className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Issue
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                    id="issue"
                    name="issue"
                    placeholder="Enter current issue"
                    value={formData.issue}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="publicationUrl" className="block text-sm font-semibold text-gray-700 mb-2">
                  Journal URL
                </label>
                <input
                  type="url"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                  id="publicationUrl"
                  name="publicationUrl"
                  placeholder="https://example.com/journal"
                  value={formData.publicationUrl}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label htmlFor="associationBodyUrl" className="block text-sm font-semibold text-gray-700 mb-2">
                  URL of Association Body Hosting the Journal
                </label>
                <input
                  type="url"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                  id="associationBodyUrl"
                  name="associationBodyUrl"
                  placeholder="https://association.example.com"
                  value={formData.associationBodyUrl}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label htmlFor="physicalAddress" className="block text-sm font-semibold text-gray-700 mb-2">
                  Physical Address *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                  id="physicalAddress"
                  name="physicalAddress"
                  placeholder="Office address (street, city, country)"
                  value={formData.physicalAddress}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label htmlFor="journalAccessibility" className="block text-sm font-semibold text-gray-700 mb-2">
                  Journal Articles Accessibility *
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                  id="journalAccessibility"
                  name="journalAccessibility"
                  value={formData.journalAccessibility}
                  onChange={handleInputChange}
                >
                  <option value="">Select accessibility…</option>
                  <option value="1">Fully open</option>
                  <option value="2">Partially</option>
                  <option value="3">Not available</option>
                </select>
              </div>

              <div>
                <label htmlFor="dateOfJournalCommencement" className="block text-sm font-semibold text-gray-700 mb-2">
                  Date of Journal Commencement
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                  id="dateOfJournalCommencement"
                  name="dateOfJournalCommencement"
                  value={formData.dateOfJournalCommencement}
                  onChange={handleInputChange}
                />
                <p className="mt-1 text-xs text-gray-400">When the journal began publishing</p>
              </div>

              <div>
                <label htmlFor="keywords" className="block text-sm font-semibold text-gray-700 mb-2">
                  Keyword(s)
                </label>
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all resize-y"
                  id="keywords"
                  name="keywords"
                  rows={3}
                  placeholder="Enter keywords separated by commas (e.g. education, research, policy)"
                  value={formData.keywords}
                  onChange={handleInputChange}
                />
                <p className="mt-1 text-xs text-gray-400">Separate each keyword with a comma</p>
              </div>



              {/* Editorial Board Members */}
              <div className="border-t border-gray-100 pt-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700">Editorial Board Members</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Add board members with their titles and locations (up to {MAX_EDITORIAL}).
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-md whitespace-nowrap">
                    <i className="bi bi-info-circle"></i>
                    {validBoardMembers.length}/{MAX_EDITORIAL} added · max {MAX_EDITORIAL}
                  </div>
                </div>

                <div className="space-y-3">
                  {formData.editorialBoardMembers.map((member, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-start bg-gray-50 border border-gray-100 rounded-lg p-3">
                      <div>
                        <label className="block text-[11px] font-semibold text-gray-500 mb-1">
                          Member #{idx + 1} — Name & Title
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                          placeholder="Add title in name, e.g. Prof. John Doe"
                          value={member.name}
                          onChange={(e) => updateBoardMember(idx, 'name', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-gray-500 mb-1">
                          Address
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all"
                          placeholder="Include country, e.g. University of Lagos, Nigeria"
                          value={member.address}
                          onChange={(e) => updateBoardMember(idx, 'address', e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeBoardMember(idx)}
                        disabled={formData.editorialBoardMembers.length <= 1}
                        title="Remove this member"
                        className="mt-5 md:mt-6 flex items-center justify-center w-9 h-9 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addBoardMember}
                  disabled={formData.editorialBoardMembers.length >= MAX_EDITORIAL}
                  className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    formData.editorialBoardMembers.length >= MAX_EDITORIAL
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-[#088A61] text-white hover:bg-[#067451]'
                  }`}
                >
                  <i className="bi bi-plus-lg"></i>
                  Add Member
                </button>

              </div>

              <div>
                <label htmlFor="abstract" className="block text-sm font-semibold text-gray-700 mb-2">
                  Abstract *
                </label>
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all resize-y"
                  id="abstract"
                  name="abstract"
                  rows={6}
                  placeholder="Provide a brief abstract of your journal support application"
                  value={formData.abstract}
                  onChange={handleInputChange}
                ></textarea>
                <div className="mt-2 text-sm text-gray-500">
                  {formData.abstract.length} characters
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  className={`px-8 py-3 rounded-lg text-sm font-bold transition-all inline-flex items-center gap-2 ${isStep1Valid
                    ? 'bg-gray-800 hover:bg-black text-white cursor-pointer active:scale-95'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  disabled={!isStep1Valid}
                  onClick={() => setActiveStep(2)}
                >
                  Continue to Upload
                  <i className="bi bi-arrow-right"></i>
                </button>
              </div>
            </form>
          </div>
        )}

        {activeStep === 2 && (
          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Upload Journal Documents
              </h2>
              <p className="text-sm text-gray-600">
                Provide the three required PDF documents below (max 20MB each).
              </p>
            </div>

            {([
              {
                key: 'manuscript' as const,
                inputId: 'file-input',
                title: '1. Journal Application (Manuscript)',
                description: 'The main journal application document.',
                file: formData.uploadedFile,
                fileName: formData.fileName,
                fileSize: formData.fileSize,
                onPick: (file: File, ext: string) => setFormData(prev => ({
                  ...prev,
                  uploadedFile: file,
                  fileType: ext,
                  fileName: file.name,
                  fileSize: file.size,
                })),
                onClear: () => setFormData(prev => ({
                  ...prev,
                  uploadedFile: null,
                  fileName: '',
                  fileType: '',
                  fileSize: 0,
                })),
              },
              {
                key: 'guidelines' as const,
                inputId: 'guidelines-input',
                title: '2. Journal Publication & Operational Guidelines for Authors',
                description: 'Document describing how authors should publish in this journal.',
                file: formData.guidelinesFile,
                fileName: formData.guidelinesFileName,
                fileSize: formData.guidelinesFileSize,
                onPick: (file: File) => setFormData(prev => ({
                  ...prev,
                  guidelinesFile: file,
                  guidelinesFileName: file.name,
                  guidelinesFileSize: file.size,
                })),
                onClear: () => setFormData(prev => ({
                  ...prev,
                  guidelinesFile: null,
                  guidelinesFileName: '',
                  guidelinesFileSize: 0,
                })),
              },
              {
                key: 'policy' as const,
                inputId: 'policy-input',
                title: '3. Journal Policy',
                description: 'Editorial and review policy for this journal.',
                file: formData.policyFile,
                fileName: formData.policyFileName,
                fileSize: formData.policyFileSize,
                onPick: (file: File) => setFormData(prev => ({
                  ...prev,
                  policyFile: file,
                  policyFileName: file.name,
                  policyFileSize: file.size,
                })),
                onClear: () => setFormData(prev => ({
                  ...prev,
                  policyFile: null,
                  policyFileName: '',
                  policyFileSize: 0,
                })),
              },
            ]).map(box => (
              <div key={box.key} className="mb-5">
                <div className="mb-2">
                  <p className="text-sm font-bold text-gray-800">{box.title} <span className="text-red-500">*</span></p>
                  <p className="text-xs text-gray-500">{box.description}</p>
                </div>

                <div
                  className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-[#088A61] hover:bg-[#088A61]/5 transition-all group"
                  onClick={() => document.getElementById(box.inputId)?.click()}
                >
                  <input
                    type="file"
                    id={box.inputId}
                    className="hidden"
                    accept=".pdf,application/pdf"
                    onChange={(e) => {
                      const picked = e.target.files?.[0];
                      if (!picked) return;
                      const ext = picked.name.split('.').pop()?.toLowerCase() || '';
                      const isPdf = ext === 'pdf' && picked.type === 'application/pdf';
                      const maxSize = 20 * 1024 * 1024;
                      if (!isPdf) {
                        showNotification('error', { title: 'Invalid File Type', message: 'Please upload a PDF only.' });
                        e.target.value = '';
                        return;
                      }
                      if (picked.size > maxSize) {
                        showNotification('error', { title: 'File Too Large', message: 'Maximum file size is 20MB.' });
                        e.target.value = '';
                        return;
                      }
                      box.onPick(picked, ext);
                    }}
                  />
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="w-10 h-10 bg-[#088A61]/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className="bi bi-cloud-arrow-up text-xl text-[#088A61]"></i>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-500">PDF only (max. 20MB)</p>
                  </div>
                </div>

                {box.file && (
                  <div className="mt-2 bg-[#088A61]/5 rounded-lg p-3 border border-[#088A61]/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-md flex items-center justify-center bg-red-500 text-white">
                          <i className="bi bi-file-earmark-pdf"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate">{box.fileName}</p>
                          <p className="text-[11px] text-gray-500 font-medium">PDF · {(box.fileSize / (1024 * 1024)).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          box.onClear();
                          const el = document.getElementById(box.inputId) as HTMLInputElement | null;
                          if (el) el.value = '';
                        }}
                        className="w-7 h-7 rounded-md flex items-center justify-center bg-white border border-red-200 text-red-500 hover:bg-red-600 hover:text-white transition-all flex-shrink-0"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div className="flex justify-between items-center bg-gray-50 -mx-8 -mb-8 p-8 rounded-b-lg border-t border-gray-100">
              <button
                type="button"
                className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-bold text-gray-700 hover:bg-white transition-all flex items-center gap-2"
                onClick={() => setActiveStep(1)}
              >
                <i className="bi bi-arrow-left"></i>
                Back
              </button>
              <button
                type="button"
                className={`px-8 py-3 rounded-lg text-sm font-bold transition-all inline-flex items-center gap-2 ${isStep2Valid
                  ? 'bg-gray-800 hover:bg-black text-white cursor-pointer active:scale-95'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                disabled={!isStep2Valid}
                onClick={() => setActiveStep(3)}
              >
                Review Submission
                <i className="bi bi-arrow-right"></i>
              </button>
            </div>
          </div>
        )}

        {activeStep === 3 && (
          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Review & Submit
              </h2>
              <p className="text-sm text-gray-600">
                Please review your submission before submitting
              </p>
            </div>

            <div className="space-y-6 mb-8">
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Name of Journal</label>
                    <p className="text-lg font-bold text-gray-900 leading-tight">
                      {formData.title}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Name of Journal Leader</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.authors}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Position of Representative</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.positionOfJournalRepresentative || <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Professional Association</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.journalProfessionalAssociation || <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      ISSN No
                    </label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.isbnNo || <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Edition</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.edition || <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Application Type</label>
                    <p className="text-sm font-bold text-gray-900">
                      Journal
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Thematic Area</label>
                    <p className="text-sm font-bold text-gray-900">
                      {categories.find(c => c.id === formData.categoryId)?.name}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Current Volume</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.volume || <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Current Issue</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.issue || <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Journal URL</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.publicationUrl || <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">URL of Association Body Hosting the Journal</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.associationBodyUrl || <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Physical Address</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.physicalAddress || <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Journal Articles Accessibility</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.journalAccessibility === '1' ? 'Fully open'
                        : formData.journalAccessibility === '2' ? 'Partially'
                        : formData.journalAccessibility === '3' ? 'Not available'
                        : <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Date of Journal Commencement</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.dateOfJournalCommencement
                        ? new Date(`${formData.dateOfJournalCommencement}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Editorial Board Members ({validBoardMembers.length})
                    </label>
                    {validBoardMembers.length === 0 ? (
                      <span className="text-gray-400 italic font-medium text-sm">None provided</span>
                    ) : (
                      <ol className="space-y-1.5 mt-1">
                        {validBoardMembers.map((m, i) => (
                          <li key={i} className="text-sm text-gray-900">
                            <span className="font-bold">{i + 1}. {m.name}</span>
                            <span className="text-gray-600"> — {m.address}</span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Abstract</label>
                    <p className="text-sm text-gray-600 leading-relaxed italic">
                      {formData.abstract}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Keyword(s)</label>
                    <p className="text-sm font-bold text-gray-900">
                      {formData.keywords
                        ? formData.keywords.split(',').map((kw, i) => (
                          <span key={i} className="inline-block bg-[#088A61]/10 text-[#088A61] text-xs font-semibold px-2 py-0.5 rounded-full mr-1 mb-1">
                            {kw.trim()}
                          </span>
                        ))
                        : <span className="text-gray-400 italic font-medium">None provided</span>}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Uploaded Documents</label>
                    <div className="space-y-2 mt-2">
                      {[
                        { label: 'Journal Application', name: formData.fileName, size: formData.fileSize },
                        { label: 'Publication & Operational Guidelines', name: formData.guidelinesFileName, size: formData.guidelinesFileSize },
                        { label: 'Journal Policy', name: formData.policyFileName, size: formData.policyFileSize },
                      ].map((doc, i) => (
                        <div key={i} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-gray-100">
                          <i className="bi bi-file-earmark-pdf text-red-500 text-xl"></i>
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{doc.label}</p>
                            <p className="text-sm font-bold text-gray-900 truncate">{doc.name || <span className="text-gray-400 italic font-normal">Not uploaded</span>}</p>
                            {doc.name && (
                              <p className="text-[10px] font-medium text-gray-400 uppercase">{(doc.size / (1024 * 1024)).toFixed(2)} MB</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center bg-gray-50 -mx-8 -mb-8 p-8 rounded-b-lg border-t border-gray-100">
              <button
                type="button"
                className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-bold text-gray-700 hover:bg-white transition-all flex items-center gap-2"
                onClick={() => setActiveStep(2)}
              >
                <i className="bi bi-arrow-left"></i>
                Modify Upload
              </button>
              <button
                type="button"
                className={`px-10 py-3 rounded-lg text-sm font-extrabold transition-all inline-flex items-center gap-3 shadow-lg ${isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#088A61] hover:bg-[#066e4d] text-white active:scale-95'
                  }`}
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Journal
                    <i className="bi bi-send-fill"></i>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationForJournalSupport;
